// src/pages/WritingPractice.js
// Notation practice: a random position appears, a move is played on the
// board (from/to squares highlighted). The student writes the move's
// notation on paper, then reveals the answer (or the 20s timer reveals it).
// "Next" deals a new position. Positions-practiced count is saved to
// `trainingResults` so coaches can see each student's history.
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import "./WritingPractice.css";

const REVEAL_SECONDS = 20;

// Build a random position, then pick a move to play. We bias toward
// "interesting" moves — captures, castling, en passant, promotion — so the
// student practises those notations, not just quiet moves.
// Returns { fenBefore, fenAfter, san, from, to, kind }.
function dealPosition() {
  for (let tries = 0; tries < 40; tries++) {
    const game = new Chess();
    const plies = 8 + Math.floor(Math.random() * 26);   // deeper so captures/castling/promotion can arise
    for (let i = 0; i < plies; i++) {
      const moves = game.moves({ verbose: true });
      if (!moves.length) break;
      game.move(moves[Math.floor(Math.random() * moves.length)]);
    }
    const legal = game.moves({ verbose: true });
    if (!legal.length) continue;                        // mate/stalemate — redeal

    const fenBefore = game.fen();

    // Categorise the legal moves so we can prefer the special ones.
    const special = legal.filter(m =>
      m.flags.includes("c") ||   // capture
      m.flags.includes("e") ||   // en passant
      m.flags.includes("k") ||   // kingside castle
      m.flags.includes("q") ||   // queenside castle
      m.flags.includes("p")      // promotion
    );
    // 75% of the time, if a special move exists, use one.
    const pool = (special.length && Math.random() < 0.75) ? special : legal;
    const pick = pool[Math.floor(Math.random() * pool.length)];

    const made = game.move({ from: pick.from, to: pick.to, promotion: pick.promotion || "q" });
    if (!made) continue;

    let kind = "move";
    if (made.flags.includes("k") || made.flags.includes("q")) kind = "castle";
    else if (made.flags.includes("e")) kind = "en passant";
    else if (made.flags.includes("p")) kind = "promotion";
    else if (made.flags.includes("c")) kind = "capture";

    return { fenBefore, fenAfter: game.fen(), san: made.san, from: made.from, to: made.to, kind };
  }
  // Fallback: a simple capture-free opening move.
  const g = new Chess();
  const before = g.fen();
  const m = g.move("e4");
  return { fenBefore: before, fenAfter: g.fen(), san: m.san, from: m.from, to: m.to, kind: "move" };
}

export default function WritingPractice() {
  const { user, userData } = useAuth();
  const [pos, setPos] = useState(null);              // { fenBefore, fenAfter, san, from, to, kind }
  const [boardFen, setBoardFen] = useState(null);    // what the board currently shows (animates on change)
  const [played, setPlayed] = useState(false);       // has the move animated yet
  const [revealed, setRevealed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(REVEAL_SECONDS);
  const [count, setCount] = useState(0);             // positions completed (Next pressed)
  const [saveState, setSaveState] = useState("");
  const timerRef = useRef(null);
  const playRef = useRef(null);
  const countRef = useRef(0);
  const savedRef = useRef(false);

  const deal = useCallback(() => {
    const p = dealPosition();
    setPos(p);
    setBoardFen(p.fenBefore);   // show the position BEFORE the move first
    setPlayed(false);
    setRevealed(false);
    setTimeLeft(REVEAL_SECONDS);
    // after a beat, animate the move so the student sees it play out
    clearTimeout(playRef.current);
    playRef.current = setTimeout(() => {
      setBoardFen(p.fenAfter);
      setPlayed(true);
    }, 900);
  }, []);

  // first position
  useEffect(() => { deal(); }, [deal]);

  // reveal countdown — only starts once the move has been played
  useEffect(() => {
    if (!pos || !played || revealed) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); setRevealed(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [pos, played, revealed]);

  const saveSession = useCallback(async (positions) => {
    if (!user || positions === 0 || savedRef.current) return;
    savedRef.current = true;
    try {
      await addDoc(collection(db, "trainingResults"), {
        studentId: user.uid,
        studentName: userData?.name || "",
        type: "writing",
        attempted: positions,
        createdAt: new Date().toISOString(),
      });
      setSaveState("saved");
    } catch (err) {
      console.error("Could not save writing-practice session:", err);
      savedRef.current = false;
      setSaveState("failed");
    }
  }, [user, userData]);

  // Best-effort save if the student leaves mid-session without pressing End.
  useEffect(() => () => {
    clearInterval(timerRef.current);
    clearTimeout(playRef.current);
    if (countRef.current > 0 && !savedRef.current) saveSession(countRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function next() {
    countRef.current += 1;
    setCount(countRef.current);
    deal();
  }

  async function endSession() {
    await saveSession(countRef.current);
    countRef.current = 0;
    setCount(0);
    savedRef.current = false;
    deal();
  }

  // Highlight the move squares only once the move has actually played.
  const highlight = pos && played ? {
    [pos.from]: { background: "rgba(200,169,110,0.55)" },
    [pos.to]: { background: "rgba(200,169,110,0.85)", boxShadow: "inset 0 0 0 3px rgba(255,215,0,0.8)" },
  } : {};

  return (
    <div className="wp-training">
      <div className="wp-top">
        <h2 className="student-section-title" style={{ padding: 0, margin: 0 }}>✍️ Writing Practice</h2>
        <div className="wp-hud">
          <span className="wp-count">📝 {count} done</span>
          {played && !revealed && pos && <span className={`wp-timer ${timeLeft <= 5 ? "low" : ""}`}>⏱ {timeLeft}s</span>}
        </div>
      </div>

      <p className="wp-hint">
        {!played
          ? "Watch carefully — a move is about to play…"
          : "Did you catch it? Write the move's notation down, then check yourself."}
      </p>

      <div className="wp-body">
        <div className="wp-board-wrap">
          {boardFen && (
            <Chessboard
              id="writing-practice-board"
              position={boardFen}
              arePiecesDraggable={false}
              animationDuration={500}
              customSquareStyles={highlight}
              customBoardStyle={{ borderRadius: 10, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
              customDarkSquareStyle={{ backgroundColor: "#7b5c3e" }}
              customLightSquareStyle={{ backgroundColor: "#e9dcc0" }}
            />
          )}
          {revealed && pos && (
            <div className="wp-answer" aria-live="polite">
              <span className="wp-answer-text">{pos.san}</span>
              {pos.kind !== "move" && <span className="wp-answer-kind">{pos.kind}</span>}
            </div>
          )}
        </div>

        <div className="wp-controls">
          {!played
            ? <button className="btn btn-secondary wp-btn" disabled>Playing move…</button>
            : !revealed
              ? <button className="btn btn-primary wp-btn" onClick={() => { clearInterval(timerRef.current); setRevealed(true); }}>Show Answer</button>
              : <button className="btn btn-primary wp-btn" onClick={next}>Next Position →</button>}
          <button className="btn btn-secondary wp-btn" onClick={() => { setBoardFen(pos.fenBefore); setPlayed(false); clearTimeout(playRef.current); playRef.current = setTimeout(() => { setBoardFen(pos.fenAfter); setPlayed(true); }, 700); setRevealed(false); setTimeLeft(REVEAL_SECONDS); }} disabled={!pos}>
            ↻ Replay move
          </button>
          <button className="btn btn-secondary wp-btn" onClick={endSession} disabled={count === 0}>
            End Session {count > 0 ? `(save ${count})` : ""}
          </button>
          <p className="wp-save-state">
            {saveState === "saved" && "✓ Session recorded"}
            {saveState === "failed" && "⚠ Couldn't save — check your connection"}
          </p>
        </div>
      </div>
    </div>
  );
}
